import { useState } from 'react';
import { PageHeader } from '@/components/shared/PageHeader';
import { ExportOperationsButton } from './ExportOperationsButton';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';
import { Download, Loader2, Plus, Trash2 } from 'lucide-react';
import {
    BUSINESS_LINES,
    COST_KINDS,
    centsToEuroInput,
    costKindLabel,
    eurosToCents,
    fmtEur,
    lineLabel,
    PeriodFilter,
    useOperationsPeriod,
    type BusinessLine,
    type CostKind,
} from './format';
import {
    downloadOpsPdf,
    uploadOpsPdf,
    useDeleteReceivedInvoice,
    useReceivedInvoices,
    useUpsertReceivedInvoice,
    type ReceivedAllocation,
    type ReceivedInvoice,
} from './hooks/useOperations';

interface DraftAllocation {
    businessLine: BusinessLine;
    costKind: CostKind;
    base: string;
    vat: string;
}

interface Draft {
    id: string | null;
    supplierName: string;
    supplierTaxId: string;
    invoiceNumber: string;
    issuedOn: string;
    notes: string;
    allocations: DraftAllocation[];
}

const EMPTY_ALLOCATION: DraftAllocation = {
    businessLine: 'store',
    costKind: 'goods',
    base: '',
    vat: '',
};

function emptyDraft(issuedOn: string): Draft {
    return {
        id: null,
        supplierName: '',
        supplierTaxId: '',
        invoiceNumber: '',
        issuedOn,
        notes: '',
        allocations: [{ ...EMPTY_ALLOCATION }],
    };
}

function draftFromInvoice(inv: ReceivedInvoice): Draft {
    return {
        id: inv.id,
        supplierName: inv.supplierName,
        supplierTaxId: inv.supplierTaxId ?? '',
        invoiceNumber: inv.invoiceNumber,
        issuedOn: inv.issuedOn,
        notes: inv.notes ?? '',
        allocations: inv.allocations.map((a) => ({
            businessLine: a.businessLine,
            costKind: a.costKind,
            base: centsToEuroInput(a.taxBaseCents),
            vat: centsToEuroInput(a.taxAmountCents),
        })),
    };
}

export function ReceivedInvoicesPage() {
    const { from, to, setPeriod } = useOperationsPeriod();
    const { data, isLoading, error, refetch } = useReceivedInvoices(from, to);
    const upsert = useUpsertReceivedInvoice();
    const remove = useDeleteReceivedInvoice();
    const [draft, setDraft] = useState<Draft | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [deleteId, setDeleteId] = useState<string | null>(null);

    async function save() {
        if (!draft) return;
        const allocations: ReceivedAllocation[] = [];
        for (const row of draft.allocations) {
            const taxBaseCents = eurosToCents(row.base);
            const taxAmountCents = eurosToCents(row.vat || '0');
            if (taxBaseCents == null || taxAmountCents == null) {
                setFormError('Revisa los importes de cada reparto.');
                return;
            }
            allocations.push({
                businessLine: row.businessLine,
                costKind: row.costKind,
                taxBaseCents,
                taxAmountCents,
                totalCents: taxBaseCents + taxAmountCents,
            });
        }
        setFormError(null);
        try {
            await upsert.mutateAsync({
                id: draft.id,
                supplierName: draft.supplierName,
                supplierTaxId: draft.supplierTaxId,
                invoiceNumber: draft.invoiceNumber,
                issuedOn: draft.issuedOn,
                notes: draft.notes,
                allocations,
            });
            setDraft(null);
        } catch (err) {
            setFormError(err instanceof Error ? err.message : 'No se pudo guardar');
        }
    }

    async function onPdf(id: string, file: File | undefined) {
        if (!file) return;
        await uploadOpsPdf('received', id, file);
        await refetch();
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title="Facturas recibidas"
                description="Gastos de las tres líneas: comisión de Stripe o Apple, compra de producto y transporte. Una factura se puede repartir entre líneas."
            >
                <button
                    type="button"
                    onClick={() => { setFormError(null); setDraft(emptyDraft(to)); }}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground"
                >
                    <Plus size={16} />
                    Nueva factura
                </button>
            </PageHeader>

            <div className="flex flex-wrap items-end gap-3">
                <PeriodFilter from={from} to={to} onChange={setPeriod} />
                <ExportOperationsButton from={from} to={to} />
            </div>
            {error && (
                <p className="text-sm text-destructive">
                    {(error as Error).message}. Hace falta desplegar la API de operaciones.
                </p>
            )}

            {draft && (
                <div className="rounded-xl border border-border p-4 space-y-4">
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        <Field label="Proveedor" value={draft.supplierName} onChange={(v) => setDraft({ ...draft, supplierName: v })} />
                        <Field label="NIF" value={draft.supplierTaxId} onChange={(v) => setDraft({ ...draft, supplierTaxId: v })} />
                        <Field label="Nº factura" value={draft.invoiceNumber} onChange={(v) => setDraft({ ...draft, invoiceNumber: v })} />
                        <label className="text-sm">
                            <span className="block text-xs text-muted-foreground mb-1">Fecha</span>
                            <input
                                type="date"
                                value={draft.issuedOn}
                                onChange={(e) => setDraft({ ...draft, issuedOn: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg bg-muted/30 border border-border text-sm"
                            />
                        </label>
                    </div>
                    <label className="text-sm block">
                        <span className="block text-xs text-muted-foreground mb-1">Notas</span>
                        <input
                            value={draft.notes}
                            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg bg-muted/30 border border-border text-sm"
                        />
                    </label>
                    <div className="space-y-2">
                        {draft.allocations.map((row, index) => (
                            <div key={index} className="grid gap-2 sm:grid-cols-5 items-end">
                                <Select
                                    label="Línea"
                                    value={row.businessLine}
                                    options={BUSINESS_LINES}
                                    onChange={(v) => {
                                        const allocations = draft.allocations.slice();
                                        allocations[index] = { ...row, businessLine: v as BusinessLine };
                                        setDraft({ ...draft, allocations });
                                    }}
                                />
                                <Select
                                    label="Tipo"
                                    value={row.costKind}
                                    options={COST_KINDS}
                                    onChange={(v) => {
                                        const allocations = draft.allocations.slice();
                                        allocations[index] = { ...row, costKind: v as CostKind };
                                        setDraft({ ...draft, allocations });
                                    }}
                                />
                                <Field
                                    label="Base €"
                                    value={row.base}
                                    onChange={(v) => {
                                        const allocations = draft.allocations.slice();
                                        allocations[index] = { ...row, base: v };
                                        setDraft({ ...draft, allocations });
                                    }}
                                />
                                <Field
                                    label="IVA €"
                                    value={row.vat}
                                    onChange={(v) => {
                                        const allocations = draft.allocations.slice();
                                        allocations[index] = { ...row, vat: v };
                                        setDraft({ ...draft, allocations });
                                    }}
                                />
                                <button
                                    type="button"
                                    className="text-xs text-muted-foreground hover:text-foreground h-10"
                                    onClick={() => {
                                        const allocations = draft.allocations.filter((_, i) => i !== index);
                                        setDraft({ ...draft, allocations: allocations.length ? allocations : [{ ...EMPTY_ALLOCATION }] });
                                    }}
                                >
                                    Quitar reparto
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            className="text-sm text-primary"
                            onClick={() => setDraft({ ...draft, allocations: [...draft.allocations, { ...EMPTY_ALLOCATION }] })}
                        >
                            Añadir reparto
                        </button>
                    </div>
                    {formError && <p className="text-sm text-destructive">{formError}</p>}
                    <div className="flex gap-2">
                        <button
                            type="button"
                            disabled={upsert.isPending}
                            onClick={() => void save()}
                            className="px-3 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground disabled:opacity-50"
                        >
                            {upsert.isPending ? 'Guardando…' : 'Guardar'}
                        </button>
                        <button type="button" onClick={() => setDraft(null)} className="px-3 py-2 rounded-lg text-sm bg-muted/50">
                            Cancelar
                        </button>
                    </div>
                </div>
            )}

            <div className="rounded-xl border border-border overflow-x-auto">
                <table className="w-full text-sm min-w-[860px]">
                    <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <tr>
                            <th className="px-4 py-3">Factura</th>
                            <th className="px-4 py-3">Fecha</th>
                            <th className="px-4 py-3">Reparto</th>
                            <th className="px-4 py-3 text-right">Base</th>
                            <th className="px-4 py-3 text-right">IVA</th>
                            <th className="px-4 py-3 text-right">Total</th>
                            <th className="px-4 py-3" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {isLoading && (
                            <tr>
                                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                                    <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                                    Cargando…
                                </td>
                            </tr>
                        )}
                        {!isLoading && (data?.length ?? 0) === 0 && !error && (
                            <tr>
                                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                                    No hay facturas recibidas en este periodo.
                                </td>
                            </tr>
                        )}
                        {data?.map((inv) => (
                            <tr key={inv.id} className="hover:bg-muted/20 align-top">
                                <td className="px-4 py-3">
                                    <div className="font-medium">{inv.supplierName}</div>
                                    <div className="text-xs text-muted-foreground">{inv.invoiceNumber}</div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">{inv.issuedOn}</td>
                                <td className="px-4 py-3 text-xs">
                                    {inv.allocations.map((a, i) => (
                                        <div key={i}>
                                            {lineLabel(a.businessLine)} · {costKindLabel(a.costKind)} · {fmtEur(a.taxBaseCents)}
                                        </div>
                                    ))}
                                </td>
                                <td className="px-4 py-3 text-right">{fmtEur(inv.taxBaseCents)}</td>
                                <td className="px-4 py-3 text-right">{fmtEur(inv.taxAmountCents)}</td>
                                <td className="px-4 py-3 text-right font-medium">{fmtEur(inv.totalCents)}</td>
                                <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                                    <button type="button" className="text-xs text-primary" onClick={() => { setFormError(null); setDraft(draftFromInvoice(inv)); }}>
                                        Editar
                                    </button>
                                    <label className="text-xs text-primary cursor-pointer">
                                        PDF
                                        <input
                                            type="file"
                                            accept="application/pdf"
                                            className="hidden"
                                            onChange={(e) => void onPdf(inv.id, e.target.files?.[0])}
                                        />
                                    </label>
                                    {inv.hasPdf && (
                                        <button
                                            type="button"
                                            className="text-xs text-primary inline-flex items-center gap-1"
                                            onClick={() => void downloadOpsPdf('received', inv.id, `${inv.invoiceNumber}.pdf`)}
                                        >
                                            <Download size={12} />
                                        </button>
                                    )}
                                    <button type="button" className="text-xs text-destructive" onClick={() => setDeleteId(inv.id)}>
                                        <Trash2 size={14} className="inline" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <AdminConfirmDialog
                open={deleteId != null}
                title="Eliminar factura recibida"
                description="Se quita del resultado del periodo. El PDF deja de estar enlazado."
                confirmLabel="Eliminar"
                variant="danger"
                onCancel={() => setDeleteId(null)}
                onConfirm={() => {
                    if (!deleteId) return;
                    void remove.mutateAsync(deleteId).finally(() => setDeleteId(null));
                }}
            />
        </div>
    );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
    return (
        <label className="text-sm">
            <span className="block text-xs text-muted-foreground mb-1">{label}</span>
            <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-muted/30 border border-border text-sm"
            />
        </label>
    );
}

function Select({
    label,
    value,
    options,
    onChange,
}: {
    label: string;
    value: string;
    options: { value: string; label: string }[];
    onChange: (v: string) => void;
}) {
    return (
        <label className="text-sm">
            <span className="block text-xs text-muted-foreground mb-1">{label}</span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-muted/30 border border-border text-sm"
            >
                {options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
        </label>
    );
}
